import {ajaxUrl, debug} from "btdt/config";
import {stringSimilarity} from "string-similarity-js";

export async function getLocations(include_service_areas) {

    try {
        const postBody = new FormData();
        postBody.set('action', 'get_locations')

        const url = new URL(ajaxUrl)

        const time = new Date().getTime() / 1000
        url.searchParams.append('t', time.toString())
        if (include_service_areas) {
            url.searchParams.append('sa', '');
        }

        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('nocache') !== null) {
            url.searchParams.append('nocache', '')
        }

        const response = await fetch(url.toString(), {
            method: "POST", body: postBody,
        })
        const locations = await response.json()

        return locations.filter(l => l.lat !== '' && l.lng !== '');
    } catch (e) {
        console.error(e);
    }
}

export async function getOverrides() {

    try {
        const postBody = new FormData();
        postBody.set('action', 'get_search_overrides')

        const response = await fetch(ajaxUrl, {
            method: "POST", body: postBody,
        })
        return await response.json()

    } catch (e) {
        console.error(e);
    }
}

export async function getCoordinates(placeName) {
    if (debug) console.log(`getting coordinates for ${placeName}`)

    try {
        const postBody = new FormData();
        postBody.set('action', 'get_coords')
        postBody.set('query', placeName)

        const response = await fetch(ajaxUrl, {
            method: "POST", body: postBody,
        })

        return await response.json();

    } catch (e) {
        console.error(e);
    }

}

export async function getPostalCode(lat, lng) {
    if (debug) console.log(`getting postal code for coords ${lat},${lng}`)

    try {
        const postBody = new FormData();
        postBody.set('action', 'get_postal_code')
        postBody.set('lat', lat)
        postBody.set('lng', lng)

        const response = await fetch(ajaxUrl, {
            method: "POST", body: postBody,
        })

        const addr = await response.json();

        return addr.components?.find(c => c.types.includes('postal_code'))?.long_name

    } catch (e) {
        console.error(e);
    }

}

/**
 * Builds a DOM element for a location
 */
export function buildLocationElement(location, {classNames = '', buttonText = 'Select', buttonCallback = null} = {}) {
    const el = document.createElement("section");

    const classes = classNames.split(' ');
    classes.forEach(className => {
        className = className.trim()
        if (className !== '') {
            el.classList.add(className)
        }
    })

    const title = document.createElement("div");
    title.innerHTML = `<strong>Bin There Dump That - ${location.location_name}</strong>`

    const phone = document.createElement("div");
    phone.innerHTML = `
            <a href="tel:${location.phone}" class="decoration-transparent hover:!decoration-brand-green">
                ${location.phone}
            </a>`

    const hours = document.createElement("div");
    hours.classList.add('small-p')

    const {
        open_now, close_time, closes_soon, hours_to_close, next_open_day, next_open_time, open_24
    } = locationHoursData(location)

    const info = []

    if (open_now) {
        if (closes_soon) {
            info.push(`Closes soon`)
            info.push(close_time)
        } else {
            if (!open_24) {
                info.push(`Open`)
                info.push(`Closes ${close_time}`)
            } else {
                info.push(`Open 24 hours`)
            }
        }
    } else {
        // only show closed text if there is a next open time. if no next open time exists, they likely did not enter hours of operation
        if (next_open_time) {
            info.push(`Closed`)
        }
    }

    if ((!open_now || closes_soon) && (next_open_day && next_open_time)) {
        info.push(`Opens ${next_open_time} ${next_open_day.substring(0, 3)}`)
    }

    hours.innerHTML = info.join(' &bull; ')

    const footer = document.createElement('footer')
    footer.classList.add('flex', 'justify-between', 'items-center', 'mt-2')

    if (location.service_area && location.parent) {
        const notice = document.createElement('div')
        notice.classList.add('small-p', 'mr-2')
        notice.innerHTML = `*Served by our ${location.parent.location_name} location`
        footer.appendChild(notice)
    }

    const btn = document.createElement("button");
    btn.classList.add('btn', 'btn-sm', 'block', 'ml-auto', 'w-fit', 'whitespace-nowrap')
    if (buttonCallback && typeof buttonCallback === 'function') {
        btn.addEventListener('click', (e) => {
            e.preventDefault()
            buttonCallback()
        })
    }
    btn.innerHTML = buttonText

    footer.appendChild(btn)

    el.appendChild(title)
    el.appendChild(phone)
    el.appendChild(hours)
    el.appendChild(footer)

    return el;
}

/**
 * Provides useful data regarding a location's hours such as whether they are currently open, when they open next, when they close and whether they are closing soon.
 */
export function locationHoursData(location) {
    let open_now, closes_soon, hours_to_close, next_open_day, next_open_time, close_time, open_24

    const today = new Date()
    const day_of_week = today.getDay()

    const today_index = location.hours?.days?.findIndex(d => parseInt(d.day_of_week) === day_of_week)
    const today_hours = location.hours.days[today_index]

    if (today_hours) {
        if (today_hours.open && today_hours.open_time && today_hours.close_time) {

            if (!location.timezone) {
                location.timezone = 'America/Edmonton';
            }

            const close_time_date = timeToDate(today_hours.close_time)
            const close_time_adj = new Date(close_time_date.toLocaleString('en-US', {timeZone: location.timezone}))

            if (today_hours.close_time === today_hours.open_time) {
                hours_to_close = 999
                closes_soon = false
                open_now = true
                open_24 = true
            } else {
                hours_to_close = (close_time_adj.getTime() - today.getTime()) / 1000 / 60 / 60
                closes_soon = hours_to_close < 1
                open_now = hours_to_close > 0
                open_24 = false
            }

            close_time = formatTime(today_hours.close_time)

        } else {
            open_now = false
            open_24 = false
        }

        if (today_hours.open_time && today_hours.close_time) {
            let i = today_index + 1
            let looped = false
            while (next_open_day === undefined) {
                if (i === 7) {
                    i = 0
                    looped = true
                }
                if (location.hours.days[i].open) {
                    next_open_day = location.hours.days[i].day
                    next_open_time = formatTime(location.hours.days[i].open_time)
                }
                i++
                if (i === 7 && looped) {
                    break
                }
            }
        }
    }

    return {
        open_now, close_time, closes_soon, hours_to_close, next_open_day, next_open_time, open_24
    }
}

/**
 * Calculates the distance between each location and the provided coordinates, then sorts by nearest location
 */
export function sortLocationsByDistance(lat, lng, locations, google) {
    // calculate user position from provided coordinates
    const p1 = new google.maps.LatLng(lat, lng);

    // calculate each location's distance from user
    locations.forEach(l => {
        const p2 = new google.maps.LatLng(l.lat, l.lng);
        l.distance = (google.maps.geometry.spherical.computeDistanceBetween(p1, p2) / 1000).toFixed(1);
    });

    // sort locations by distance
    locations.sort((a, b) => {
        if (parseFloat(a.distance) < parseFloat(b.distance)) {
            return -1;
        }

        if (parseFloat(a.distance) > parseFloat(b.distance)) {
            return 1;
        }

        return 0;
    });

    return locations
}

/**
 * Converts 24hr time string into Date object
 * @param timeStr
 * @returns {Date}
 */
export function timeToDate(timeStr) {
    const times = timeStr.split(':')
    const date = new Date()
    date.setHours(parseInt(times[0]), parseInt(times[1]))
    return date
}

/**
 * Converts 24h time string into user-friendly string
 * 16:00 -> 4:00 PM
 * @param timeStr
 * @returns {string}
 */
export function formatTime(timeStr) {
    const date = timeToDate(timeStr)
    return date.toLocaleTimeString('en-US', {timeStyle: 'short'})
}

export function parseAddressComponents(addr) {
    const country = addr?.find(c => c.types.includes('country'))?.short_name?.toLowerCase()
    const unitNumber = addr?.find(c => c.types.includes('subpremise'))?.long_name
    const streetNumber = addr?.find(c => c.types.includes('street_number'))?.long_name
    const streetName = addr?.find(c => c.types.includes('route'))?.short_name
    const city = addr?.find(c => c.types.includes('locality') || c.types.includes('sublocality'))?.short_name
    const region = addr?.find(c => c.types.includes('administrative_area_level_1'))?.short_name
    const postalCode = addr?.find(c => c.types.includes('postal_code'))?.long_name
    return {
        address: [streetNumber, streetName, unitNumber ? `#${unitNumber}` : ''].join(' ').trim(),
        city,
        region,
        postalCode,
        country
    }
}

export async function getCoordsByPostalCode(postalCode) {

    const formData = new FormData();

    formData.set('action', 'get_coords')
    formData.set('query', postalCode)

    const response = await fetch(ajaxUrl, {
        method: "POST", body: formData,
    })

    const result = await response.json();

    if (result.status === 'success') {
        return result.coords
    } else {
        throw Error('Oops! We could not determine your location. Please make sure you have entered a valid address and try again.')
    }

}

export async function matchUserToOverrides(city, region, locations){
    if (debug) console.log('no address provided, trying to match based on overrides', city, region)
    let matchedOverride = locations.filter(l => l.overrides.findIndex(o => o.city === city && o.region === region) !== -1)
    if (debug) console.log('search overrides:', matchedOverride)
    return matchedOverride
}

export async function matchUserToLocation(userLocation, locations, google) {

    let {city, postalCode, country, coords} = userLocation

    if (debug) console.log('attempting to match franchise to: ', userLocation)

    // filter the locations by country
    let filteredLocations = locations.filter(l => l.country === country)

    try {
        // attempt to get the coordinates from the postal code if none were provided
        if (!userLocation.coords?.lat || !userLocation.coords?.lng) {
            if (debug) console.log('no coords provided. getting coords from postal code')
            coords = await getCoordsByPostalCode(postalCode)
            if (debug) console.log('resolved coordinates, ', coords)
        }
        // sort the locations by distance
        filteredLocations = sortLocationsByDistance(coords.lat, coords.lng, filteredLocations, google)
    } catch (e) {
        console.error(e)
        return false
    }

    // attempt to filter the locations by city or zip
    let matchedLocations = filteredLocations.filter(location => {
        if (country === 'us') {
            // if the USA, just check if the postal code is in the "zip codes served"
            return location.serves.includes(postalCode)
        } else {
            // if in Canada, perform a string similarity match to on the city name (more reliable than direct string comparison)
            const similarity = stringSimilarity(city, location.city)
            const matchThreshold = 0.7 // (0 = no similarity, 1 = perfect match)

            // if the city name doesn't match, check the "cities served" and service areas
            if (similarity < matchThreshold) {
                const servesMatches = location.serves.filter(c => stringSimilarity(city, c) >= matchThreshold)
                if (servesMatches.length > 0) {
                    return true
                }
                const areasMatches = location.service_areas.filter(c => stringSimilarity(city, c) >= matchThreshold)
                if (areasMatches.length > 0) {
                    return true
                }
            }
            return similarity >= matchThreshold
        }
    })

    if (matchedLocations.length === 0) {
        if (debug) console.log('did not match to any franchises, returning all sorted by distance', filteredLocations)
        return filteredLocations
    }

    if (debug) console.log('matched franchises: ', matchedLocations)
    return matchedLocations
}

/**
 * Shuffles an array
 * @param array
 */
export function shuffleArray(array) {
    for (let i = array.length - 1; i >= 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}

/**
 * Joins an array of strings into a single comma separated string, followed by an oxford comma and conjunction
 * @param arr
 * @param conjunction
 * @param ifempty
 * @returns {*}
 */
export function oxford(arr, conjunction, ifempty) {
    let l = arr.length;
    if (!l) return ifempty;
    if (l < 2) return arr[0];
    if (l < 3) return arr.join(` ${conjunction} `);
    arr = arr.slice();
    arr[l - 1] = `${conjunction} ${arr[l - 1]}`;
    return arr.join(", ");
}

/**
 * Compares two objects
 * @param x
 * @param y
 * @returns {boolean}
 */
export function objectEquals( x, y ) {
    // if both x and y are null or undefined and exactly the same
    if ( x === y ) return true;

    // if they are not strictly equal, they both need to be Objects
    if ( ! ( x instanceof Object ) || ! ( y instanceof Object ) ) return false;

    // they must have the exact same prototype chain, the closest we can do is test their constructor.
    if ( x.constructor !== y.constructor ) return false;

    for ( var p in x ) {
        // other properties were tested using x.constructor === y.constructor
        if ( ! x.hasOwnProperty( p ) ) continue;

        // allows to compare x[ p ] and y[ p ] when set to undefined
        if ( ! y.hasOwnProperty( p ) ) return false;

        // if they have the same strict value or identity then they are equal
        if ( x[ p ] === y[ p ] ) continue;

        // Numbers, Strings, Functions, Booleans must be strictly equal
        if ( typeof( x[ p ] ) !== "object" ) return false;

        // Objects and Arrays must be tested recursively
        if ( ! objectEquals( x[ p ],  y[ p ] ) ) return false;
    }

    // allows x[ p ] to be set to undefined
    for ( p in y )
        if ( y.hasOwnProperty( p ) && ! x.hasOwnProperty( p ) )
            return false;


    return true;
}

export function segmentTrack(event, properties) {
    if (debug) console.log('attempting segment track')
    return new Promise((resolve, reject) => {
        const callback = () => {
            if (debug) console.log('segment track success')
            resolve(true)
        }
        if (!window.analytics || !window.analytics.options) {
            if (debug) console.log('segment analytics.js is not loaded')
            resolve(false)
        } else {
            window.analytics.track(event, properties, {}, callback)
        }
    });
}

export function dataLayerPush(event, params) {
    if (debug) console.log('attempting gtm datalayer push')
    return new Promise((resolve, reject) => {
        window.dataLayer = window.dataLayer || []
        const callback = () => {
            if (debug) console.log('gtm datalayer push success')
            resolve(true)
        }
        if (!window.dataLayer.find(e => e.event && e.event === 'gtm.load')) {
            if (debug) console.log('gtm is not loaded')
            resolve(false)
        } else {
            window.dataLayer.push({event: event, ...params, eventCallback: callback, eventTimeout: 2000})
        }
    });

}